import React, { useEffect, useState } from 'react'
import { node, bool, string, func } from 'prop-types'
import styled, { css } from 'styled-components'
import { rgba } from 'polished'

import * as spacing from 'styles/spacing'
import { media, mediaDown } from 'styles/media'
import { black, white } from 'styles/colors'
import { animationTime, animationCurve, borderRadius } from 'styles/global'
import Icon from 'components/Icon'
import Portal from 'components/Portal'

import CloseIcon from 'icons/close.svg'

const Container = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: auto;
  transition: opacity ${animationTime} ${animationCurve}, visibility ${animationTime} ${animationCurve};
  background-color: ${rgba(black, 0.75)};
  opacity: ${props => (props.visible && 1) || 0};
  visibility: ${props => (props.visible && 'visible') || 'hidden'};
  pointer-events: ${props => (props.visible && 'auto') || 'none'};
  z-index: 900;
  -webkit-overflow-scrolling: ${props => (props.visible && 'touch') || 'auto'};
`

const Table = styled.div`
  display: table;
  table-layout: fixed;
  height: 100%;
  width: 100%;
`

const Cell = styled.div`
  display: table-cell;
  height: 100%;
  width: 100%;
  vertical-align: middle;
  padding: ${spacing.medium};

  ${media.small`
    padding: ${spacing.large};
  `};

  ${media.medium`
    padding: ${spacing.xLarge};
  `};

  ${media.large`
    padding: ${spacing.xxxLarge};
  `};
`

const Inner = styled.div`
  position: relative;
  margin: 0 auto;
  width: 100%;
  max-width: 600px;
  background: ${white};
  border-radius: ${borderRadius};
  overflow: hidden;
  opacity: 0;
  transform: ${props => (props.visible && 'none') || 'translateY(15px)'};
  transition: transform ${animationTime} ${animationCurve}, opacity ${animationTime} ${animationCurve};
  z-index: 450;
  background-color: ${white};

  ${props =>
    props.isVideo &&
    css`
      padding-top: 0 !important;
    `};

  ${props =>
    props.wide &&
    css`
      max-width: 900px;
    `};

  ${mediaDown.medium`
    padding-top: ${spacing.small};
  `};

  ${props =>
    props.visible &&
    css`
      opacity: 1;
      transform: none;
    `};
`

const Close = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 450;
  padding: ${spacing.medium};
  display: flex;

  ${props =>
    props.isWhite &&
    css`
      color: ${white};
    `};

  ${props =>
    props.isVideo &&
    css`
      position: fixed;
      color: ${white};
      background-color: ${rgba(white, 0.1)};
      border-radius: 50%;
      top: ${spacing.medium};
      right: ${spacing.medium};

      ${media.medium`
        top: ${spacing.large};
        right: ${spacing.large};
      `};
    `};
`

const Section = styled.section`
  padding: ${spacing.large};

  & + & {
    padding-top: 0;

    ${media.small`
      margin-top: -${spacing.small};
    `};

    ${media.large`
      margin-top: -${spacing.medium};
    `};
  }

  ${media.small`
    padding: ${spacing.xLarge};
  `};

  ${media.large`
    padding: ${spacing.xxLarge};
  `};
`

const SectionInner = styled.div`
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
`

const Modal = ({ children, wide, visible, isVideo, hasWhiteClose, close }) => {
  const [contentVisible, setContentVisible] = useState(visible)
  useEffect(() => {
    if (visible) setContentVisible(visible)
    if (!visible) setTimeout(() => setContentVisible(visible), 300)
  }, [visible])

  useEffect(() => {
    document.body.style.overflow = visible ? 'hidden' : 'visible'
  }, [visible])

  return (
    <Portal selector={'modals'}>
      <Container visible={visible}>
        <Table>
          <Cell>
            <Inner visible={visible} wide={wide} isVideo={isVideo}>
              {!isVideo && close && (
                <Close onClick={close} isVideo={isVideo} type="button" isWhite={hasWhiteClose}>
                  <Icon icon={<CloseIcon />} />
                </Close>
              )}
              {contentVisible && children}
            </Inner>
          </Cell>
        </Table>
        {isVideo && (
          <Close onClick={close} isVideo={isVideo} isWhite={hasWhiteClose} type="button">
            <Icon icon={<CloseIcon />} />
          </Close>
        )}
      </Container>
    </Portal>
  )
}

Modal.propTypes = {
  children: node.isRequired,
  visible: bool,
  hasWhiteClose: bool,
  isVideo: bool,
  wide: bool,
  close: func
}

export const ModalSection = ({ children, className }) => (
  <Section className={className}>
    <SectionInner>{children}</SectionInner>
  </Section>
)

ModalSection.propTypes = {
  children: node.isRequired,
  className: string
}

export default Modal
